import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  TableContainer,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Snackbar,
  Alert,
  FormControl,
  Select,
  InputLabel,
  OutlinedInput,
  Checkbox,
  ListItemText,
  InputBase,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useNavigate, useLocation } from "react-router-dom";
import { Icon } from "../../icons";
import { COLORS, TYPOGRAPHY } from "../../constants/color";
import CustomTooltip from "../../components/CustomTooltip";
import { useAuth } from "../../context/authContext";
import axios from "axios";
import { url } from "../../constants/url";
import { format } from "date-fns";

const statusStyles = {
  "On Track": {
    bgcolor: "#8CD47E",
    color: "#ffffff",
  },
  "Report Missing": {
    bgcolor: "#FF6961",
    color: "#ffffff",
  },
  "DRAFT": {
    bgcolor: "#F8D66D",
    color: "#ffffff",
  },
  "SUBMITTED": {
    bgcolor: "#FFB54C",
    color: "#ffffff",
  },
  "APPROVED": {
    bgcolor: "#8CD47E",
    color: "#ffffff",
  },
  "REJECTED": {
    bgcolor: "#FF6961",
    color: "#ffffff",
  },
  "ONTRACK": {
    bgcolor: "#8CD47E",
    color: "#ffffff",
  },
  "REPORTMISSING": {
    bgcolor: "#FF6961",
    color: "#ffffff",
  },
};

// Updated headers - Removed Section column
const headers = [
  { key: "Project Name", sortable: true, field: "projectName" },
  { key: "Start Date", sortable: true, field: "projectStartDate" },
  { key: "End Date", sortable: true, field: "projectEndDate" },
  { key: "Engineer", sortable: true, field: "assignedEngineerName" },
  { key: "DPR Date", sortable: true, field: "reportDate" },
  { key: "Status", sortable: false, field: "projectStatus" }, // Changed from status to projectStatus
  { key: "Actions", sortable: false },
];

const DPR = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = location;

  // Get auth context
  const { token, userData, loading: authLoading } = useAuth() || {};

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  // Check user role
  const isEngineerRole = userData?.role === "ENGINEER";
  const isAdminOrSuperAdmin = userData?.role === "ADMIN" || userData?.role === "SUPERADMIN";

  // State for data and loading
  const [dprs, setDprs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [engineers, setEngineers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // State for pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalDprs, setTotalDprs] = useState(0);
  const [pageSize] = useState(10);

  // State for UI controls
  const [sortAnchorEl, setSortAnchorEl] = useState(null);
  const [calendarAnchorEl, setCalendarAnchorEl] = useState(null);

  // Sort and filter states
  const [sortConfig, setSortConfig] = useState(() => {
    const savedSort = localStorage.getItem("dprSort");
    return savedSort ? JSON.parse(savedSort) : {
      field: "reportDate",
      direction: "desc"
    };
  });

  const [filters, setFilters] = useState(() => {
    const savedFilters = localStorage.getItem("dprFilters");
    return savedFilters ? JSON.parse(savedFilters) : {
      search: "",
    };
  });

  const [selectedProjects, setSelectedProjects] = useState(state?.projectId ? [state.projectId] : []);
  const [selectedEngineers, setSelectedEngineers] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [showFilters, setShowFilters] = useState(!!state?.projectId);

  // Date filter states
  const [dateFilter, setDateFilter] = useState("ALL");
  const [customDateRange, setCustomDateRange] = useState({
    from: null,
    to: null,
  });
  const [showDatePicker, setShowDatePicker] = useState(false);

  // Filter menu states
  const [filterMenusOpen, setFilterMenusOpen] = useState({
    projects: false,
    engineers: false,
    statuses: false,
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Download loading state
  const [downloadingId, setDownloadingId] = useState(null);

  // Save sort config to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("dprSort", JSON.stringify(sortConfig));
  }, [sortConfig]);

  // Save filters to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem("dprFilters", JSON.stringify(filters));
  }, [filters]);

  // Fetch projects for filter dropdown
  const fetchProjects = useCallback(async () => {
    try {
      const response = await axios.get(`${url}/api/inhouse-projects/fetch`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit: 1000 }
      });

      if (response.data.projects) {
        const projectsWithEngineers = response.data.projects.map(project => ({
          ...project,
          assignedEngineerName: project.assignedEngineer?.name || project.assignedEngineerName || "Unassigned"
        }));
        setProjects(projectsWithEngineers);
      }
    } catch (err) {
      console.error("Error fetching projects:", err);
    }
  }, [token]);

  // Fetch engineers for filter dropdown
  const fetchEngineers = useCallback(async () => {
    try {
      const response = await axios.get(`${url}/api/users/fetch`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { role: "ENGINEER", limit: 1000 }
      });

      if (response.data.users) {
        setEngineers(response.data.users);
      }
    } catch (err) {
      console.error("Error fetching engineers:", err);
    }
  }, [token]);

  // Fetch DPRs from backend - USING MERGED API
  const fetchDPRs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Build query parameters
      const params = {
        page: page,
        limit: pageSize,
      };

      // Add search parameter
      if (filters.search && filters.search.trim() !== "") {
        params.search = filters.search.trim();
      }

      // Add project filter (multiple IDs as comma-separated string)
      if (selectedProjects.length > 0) {
        params.projectId = selectedProjects.join(",");
      }

      // Add engineer filter (multiple IDs as comma-separated string)
      if (selectedEngineers.length > 0) {
        params.engineerId = selectedEngineers.join(",");
      }

      // Add DPR status filter
      const dprStatuses = selectedStatuses.filter(s =>
        ["DRAFT", "SUBMITTED", "APPROVED", "REJECTED"].includes(s)
      );
      if (dprStatuses.length > 0) {
        params.status = dprStatuses.join(",");
      }

      // Add project status filter (ONTRACK, REPORTMISSING)
      const projectStatuses = selectedStatuses.filter(s =>
        ["ONTRACK", "REPORTMISSING"].includes(s)
      );
      if (projectStatuses.length > 0) {
        params.projectStatus = projectStatuses.join(",");
      }

      // Handle date filter options
      if (dateFilter !== "ALL") {
        let fromDate = null;
        let toDate = null;

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        switch (dateFilter) {
          case "TODAY":
            fromDate = new Date(today);
            toDate = new Date(today);
            toDate.setHours(23, 59, 59, 999);
            break;
          case "YESTERDAY":
            const yesterday = new Date(today);
            yesterday.setDate(yesterday.getDate() - 1);
            fromDate = yesterday;
            toDate = yesterday;
            toDate.setHours(23, 59, 59, 999);
            break;
          case "TOMORROW":
            const tomorrow = new Date(today);
            tomorrow.setDate(tomorrow.getDate() + 1);
            fromDate = tomorrow;
            toDate = tomorrow;
            toDate.setHours(23, 59, 59, 999);
            break;
          case "CUSTOM":
            if (customDateRange.from && customDateRange.to) {
              fromDate = new Date(customDateRange.from);
              fromDate.setHours(0, 0, 0, 0);
              toDate = new Date(customDateRange.to);
              toDate.setHours(23, 59, 59, 999);
            }
            break;
        }

        if (fromDate && toDate) {
          params.dateFrom = fromDate.toISOString();
          params.dateTo = toDate.toISOString();
        }
      }

      // Add sort parameters based on sortConfig - FIXED: Default to latest first
      let sortParam = "dateDesc"; // Default to latest first
      if (sortConfig.field === "projectName") {
        sortParam = sortConfig.direction === "asc" ? "projectAz" : "projectZa";
      } else if (sortConfig.field === "reportDate") {
        sortParam = sortConfig.direction === "asc" ? "dateAsc" : "dateDesc";
      } else if (sortConfig.field === "assignedEngineerName") {
        sortParam = sortConfig.direction === "asc" ? "titleAsc" : "titleDesc";
      } else if (sortConfig.field === "createdAt") {
        sortParam = sortConfig.direction === "asc" ? "oldest" : "latest";
      }

      params.sort = sortParam;

      console.log("Fetching DPRs with params:", params);

      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
        params: params,
      };

      // Use the new merged API endpoint
      const response = await axios.get(`${url}/api/dpr/merged`, config);

      if (response.data.mergedDprs) {
        setDprs(response.data.mergedDprs);
        setTotalDprs(response.data.total || response.data.mergedDprs.length);
        setTotalPages(response.data.totalPages || Math.ceil((response.data.total || response.data.mergedDprs.length) / pageSize));
      } else {
        setDprs([]);
        setTotalDprs(0);
        setTotalPages(1);
      }

    } catch (err) {
      console.error("Error fetching DPRs:", err);
      setError("Failed to fetch DPR data");
      showSnackbar("Failed to fetch DPR data. Please try again.", "error");
      setDprs([]);
      setTotalDprs(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [
    page, pageSize, token, sortConfig, filters,
    selectedProjects, selectedEngineers, selectedStatuses,
    dateFilter, customDateRange
  ]);

  // Initial fetch - only when auth is loaded and user data is available
  useEffect(() => {
    if (!authLoading && userData && token) {
      fetchDPRs();
      fetchProjects();
      fetchEngineers();
    }
  }, [fetchDPRs, fetchProjects, fetchEngineers, authLoading, userData, token]);

  // Check if any filter is applied
  const isFilterApplied = () => {
    return (
      filters.search !== "" ||
      selectedProjects.length > 0 ||
      selectedEngineers.length > 0 ||
      selectedStatuses.length > 0 ||
      dateFilter !== "ALL" ||
      sortConfig.field !== "reportDate" ||
      sortConfig.direction !== "desc"
    );
  };

  // Handle sorting
  const handleSort = (field) => {
    const newSortConfig = {
      field,
      direction: sortConfig.field === field && sortConfig.direction === "asc" ? "desc" : "asc"
    };
    setSortConfig(newSortConfig);
    setPage(1);
  };

  // Get sort icon for a specific field
  const getSortIcon = (field) => {
    if (sortConfig.field === field) {
      return sortConfig.direction === "asc" ? "foldUp" : "foldDown";
    }
    return "sort";
  };

  // Handle global sort selection
  const handleGlobalSort = (sortType) => {
    let field, direction;

    switch (sortType) {
      case "LATEST":
        field = "reportDate";
        direction = "desc";
        break;
      case "OLDEST":
        field = "reportDate";
        direction = "asc";
        break;
      case "AZ":
        field = "projectName";
        direction = "asc";
        break;
      case "ZA":
        field = "projectName";
        direction = "desc";
        break;
      default:
        field = "reportDate";
        direction = "desc";
    }

    setSortConfig({ field, direction });
    setSortAnchorEl(null);
    setPage(1);
  };

  // Handle calendar filter selection
  const handleCalendarFilterSelect = (filterValue) => {
    if (filterValue === "CUSTOM") {
      setShowDatePicker(true);
      setCalendarAnchorEl(null);
    } else {
      setDateFilter(filterValue);
      setCalendarAnchorEl(null);
      setPage(1);
    }
  };

  // Handle custom date range selection
  const handleCustomDateRangeSelect = () => {
    if (customDateRange.from && customDateRange.to) {
      setDateFilter("CUSTOM");
      setShowDatePicker(false);
      setPage(1);
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      search: "",
    });
    setSelectedProjects([]);
    setSelectedEngineers([]);
    setSelectedStatuses([]);
    setDateFilter("ALL");
    setCustomDateRange({ from: null, to: null });
    setSortConfig({
      field: "reportDate",
      direction: "desc"
    });
    setPage(1);
  };

  // Clear search only
  const clearSearch = () => {
    setFilters({ ...filters, search: "" });
    setPage(1);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Get status display text - UPDATED: Use projectStatus from API
  const getStatusText = (status) => {
    switch (status) {
      case "ONTRACK":
        return "On Track";
      case "REPORTMISSING":
        return "Report Missing";
      case "DRAFT":
        return "Draft";
      case "SUBMITTED":
        return "Submitted";
      case "APPROVED":
        return "Approved";
      case "REJECTED":
        return "Rejected";
      default:
        return status || "On Track";
    }
  };

  // Handle download DPR with loading state
  const handleDownloadDPR = async (dpr) => {
    try {
      const projectId = typeof dpr.projectId === 'object' ? dpr.projectId._id : dpr.projectId;
      const reportDate = dpr.reportDate;

      if (!projectId || !reportDate) {
        showSnackbar("Missing project or date information", "error");
        return;
      }

      setDownloadingId(dpr._id);

      const formattedDate = format(new Date(reportDate), "yyyy-MM-dd");

      const response = await axios.get(`${url}/api/dpr/download-report/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { reportDate: formattedDate },
        responseType: 'blob'
      });

      // Create download link
      const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = downloadUrl;

      // Generate filename with project name and date
      const projectName = getProjectName(dpr).replace(/[^a-zA-Z0-9]/g, '_');
      link.setAttribute('download', `DPR_${projectName}_${formattedDate}.pdf`);

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

      showSnackbar("DPR report downloaded successfully!", "success");
    } catch (error) {
      console.error("Error downloading DPR:", error);
      showSnackbar("Failed to download DPR report. Please try again.", "error");
    } finally {
      setDownloadingId(null);
    }
  };

  // Handle edit DPR - Navigate with report date
  const handleEditDPR = (dpr) => {
    const projectId = typeof dpr.projectId === 'object' ? dpr.projectId._id : dpr.projectId;
    const reportDate = dpr.reportDate;

    if (!projectId || !reportDate) {
      showSnackbar("Missing project or date information", "error");
      return;
    }

    // Format the date for URL
    const formattedDate = format(new Date(reportDate), 'yyyy-MM-dd');

    // Navigate to edit screen with both projectId and date
    navigate(`/progress/edit-dpr/${projectId}`, {
      state: {
        projectId: projectId,
        date: reportDate,
        projectName: getProjectName(dpr)
      }
    });
  };

  // Handle add DPR
  const handleAddDPR = () => {
    navigate(`/progress/add-dpr`);
  };

  // Snackbar functions
  const showSnackbar = (message, severity) => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Responsive table headers
  const getVisibleHeaders = () => {
    if (isMobile) {
      return headers.filter(header =>
        ["Project Name", "DPR Date", "Status", "Actions"].includes(header.key)
      );
    }
    if (isTablet) {
      return headers.filter(header =>
        !["Start Date", "End Date"].includes(header.key)
      );
    }
    return headers;
  };

  // Get project display name safely
  const getProjectName = (dpr) => {
    if (typeof dpr.projectId === 'object' && dpr.projectId !== null) {
      return dpr.projectId.projectName || dpr.projectName || "N/A";
    }
    return dpr.projectName || "N/A";
  };

  // Get site address safely
  const getSiteAddress = (dpr) => {
    if (typeof dpr.projectId === 'object' && dpr.projectId !== null) {
      return dpr.projectId.siteAddress || dpr.siteAddress || "-";
    }
    return dpr.siteAddress || "-";
  };

  // Get engineer name safely
  const getEngineerName = (dpr) => {
    if (typeof dpr.assignedEngineer === 'object' && dpr.assignedEngineer !== null) {
      return dpr.assignedEngineer.name || dpr.assignedEngineerName || "Unassigned";
    }
    return dpr.assignedEngineerName || "Unassigned";
  };

  // Get status from projectStatus - UPDATED: Use projectStatus field from API
  const getStatus = (dpr) => {
    // Use projectStatus if available, otherwise fall back to status
    return dpr.projectStatus || dpr.status || "ONTRACK";
  };

  // Responsive column rendering
  const renderTableCell = (header, dpr) => {
    const getFieldValue = (field) => {
      switch (field) {
        case "projectName":
          return getProjectName(dpr);
        case "projectStartDate":
          return formatDate(dpr.projectStartDate);
        case "projectEndDate":
          return formatDate(dpr.projectEndDate);
        case "assignedEngineerName":
          return getEngineerName(dpr);
        case "reportDate":
          return formatDate(dpr.reportDate);
        case "projectStatus": // UPDATED: Use projectStatus
          return getStatusText(getStatus(dpr));
        default:
          return dpr[field] || "-";
      }
    };

    switch (header.key) {
      case "Project Name":
        return (
          <TableCell key={header.key}>
            <Box>
              <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT }}>
                {getFieldValue("projectName")}
              </Typography>
              <Typography
                sx={{
                  ...TYPOGRAPHY.BODY_SMALL,
                  color: COLORS.MUTED_TEXT,
                }}
              >
                {getSiteAddress(dpr)}
              </Typography>
            </Box>
          </TableCell>
        );

      case "Start Date":
      case "End Date":
      case "Engineer":
      case "DPR Date":
        return (
          <TableCell key={header.key} sx={{ color: COLORS.TEXT, ...TYPOGRAPHY.BODY_LARGE }}>
            {getFieldValue(header.field)}
          </TableCell>
        );

      case "Status":
        const status = getStatus(dpr);
        const statusText = getStatusText(status);
        return (
          <TableCell key={header.key}>
            <Chip
              label={statusText}
              sx={{
                ...(statusStyles[status] || statusStyles["ONTRACK"]),
                ...TYPOGRAPHY.BODY_SMALL,
                height: 22,
                fontWeight: 500,
                borderRadius: 4,
                '& .MuiChip-label': {
                  px: 1.5,
                }
              }}
            />
          </TableCell>
        );

      case "Actions":
        return (
          <TableCell key={header.key}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>

              {/* Download button with loading */}
              <CustomTooltip title="Download DPR Report">
                <IconButton
                  size="small"
                  onClick={() => handleDownloadDPR(dpr)}
                  disabled={downloadingId === dpr._id}
                >
                  {downloadingId === dpr._id ? (
                    <CircularProgress size={16} />
                  ) : (
                    <Icon name="download1" color={COLORS.PRIMARY} size={20} />
                  )}
                </IconButton>
              </CustomTooltip>

              {/* Edit button */}
              {(isAdminOrSuperAdmin) && (
                <CustomTooltip title="Edit DPR">
                  <IconButton
                    size="small"
                    onClick={() => handleEditDPR(dpr)}
                  >
                    <Icon name="Action3" color={COLORS.PRIMARY} size={20} />
                  </IconButton>
                </CustomTooltip>
              )}

            </Box>
          </TableCell>
        );

      default:
        return null;
    }
  };

  // Calculate displayed range for pagination
  const startIndex = (page - 1) * pageSize + 1;
  const endIndex = Math.min(page * pageSize, totalDprs);

  // Get date filter display text
  const getDateFilterDisplay = () => {
    if (dateFilter === "ALL") return "All Dates";
    if (dateFilter === "TODAY") return "Today";
    if (dateFilter === "YESTERDAY") return "Yesterday";
    if (dateFilter === "TOMORROW") return "Tomorrow";
    if (dateFilter === "CUSTOM" && customDateRange.from && customDateRange.to) {
      const from = formatDate(customDateRange.from);
      const to = formatDate(customDateRange.to);
      return `${from} - ${to}`;
    }
    return "Select Date";
  };

  // Show loading while auth is loading
  if (authLoading) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '50vh'
      }}>
        <CircularProgress />
      </Box>
    );
  }

  // Show login prompt if not authenticated
  if (!authLoading && !userData) {
    return (
      <Box sx={{
        p: 3,
        textAlign: 'center'
      }}>
        <Typography variant="h6" color="error" sx={{ mb: 2 }}>
          Please log in to view DPRs
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate('/login')}
          sx={{ mt: 2 }}
        >
          Go to Login
        </Button>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            mb: 3,
            gap: { xs: 2, sm: 2 },
          }}
        >
          <Typography sx={{ ...TYPOGRAPHY.H2, color: COLORS.PRIMARY }}>
            Daily Progress Reports (DPR)
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              ml: { sm: "auto" },
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            {/* Search Bar */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flex: { xs: 1, sm: 'none' },
                minWidth: { xs: '100%', sm: 300 },
                px: 2,
                py: 0.7,
                borderRadius: 1.5,
                border: `1px solid ${COLORS.PRIMARY}`,
                backgroundColor: COLORS.BACKGROUND,
                color: COLORS.PRIMARY,
              }}
            >
              <Icon name="search" sx={{ fontSize: 18, color: "currentColor" }} />
              <InputBase
                placeholder="Search DPRs..."
                value={filters.search}
                onChange={(e) => {
                  setFilters({ ...filters, search: e.target.value });
                  setPage(1);
                }}
                sx={{
                  flex: 1,
                  fontFamily: 'inherit',
                  ...TYPOGRAPHY.BODY_STANDARD,
                  color: COLORS.TEXT,
                  ml: 1,
                }}
              />
              {filters.search && (
                <IconButton
                  size="small"
                  onClick={clearSearch}
                  sx={{ padding: 0.5 }}
                >
                  <Icon name="close2" sx={{ fontSize: 14 }} color={COLORS.ERROR} />
                </IconButton>
              )}
            </Box>

            {/* Calendar Menu - Show active filter */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CustomTooltip title="Date Filter">
                <IconButton
                  onClick={(e) => setCalendarAnchorEl(e.currentTarget)}
                  sx={{
                    color: dateFilter !== "ALL" ? COLORS.PRIMARY : COLORS.SECONDARY,
                  }}
                >
                  <Icon name="calendar2" />
                </IconButton>
              </CustomTooltip>
            </Box>

            {/* Sort Menu - Show active filter */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CustomTooltip title="Sort Options">
                <IconButton
                  onClick={(e) => setSortAnchorEl(e.currentTarget)}
                  sx={{
                    color: sortConfig.direction !== "desc" ? COLORS.PRIMARY : COLORS.SECONDARY,
                  }}
                >
                  <Icon name="sort" />
                </IconButton>
              </CustomTooltip>
            </Box>

            {/* Filter Button - Toggles filter section */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CustomTooltip title="Show Filters">
                <IconButton
                  onClick={() => setShowFilters(!showFilters)}
                  sx={{
                    color: showFilters ? COLORS.PRIMARY : COLORS.SECONDARY,
                  }}
                >
                  <Icon name="list" />
                </IconButton>
              </CustomTooltip>
            </Box>

            {/* Refresh / Clear */}
            <CustomTooltip title={isFilterApplied() ? "Clear Filters" : "Refresh"}>
              <IconButton
                onClick={isFilterApplied() ? clearFilters : fetchDPRs}
              >
                <Icon
                  name="rotate"
                  color={isFilterApplied() ? COLORS.ERROR : COLORS.SECONDARY}
                />
              </IconButton>
            </CustomTooltip>
          </Box>
        </Box>

        {/* Mobile Add DPR Button */}
        {(isAdminOrSuperAdmin || isEngineerRole) && (
          <Box sx={{ display: { xs: 'flex', sm: 'none' }, justifyContent: 'center', mb: 2 }}>
            <Button
              startIcon={<Icon name="plus2" />}
              sx={{
                bgcolor: COLORS.SECONDARY,
                color: "#fff",
                textTransform: "none",
                borderRadius: 2,
                ...TYPOGRAPHY.H4,
                px: 2.5,
                "&:hover": { bgcolor: COLORS.SECONDARY },
                width: '100%',
              }}
              onClick={handleAddDPR}
            >
              Add New DPR
            </Button>
          </Box>
        )}

        {/* Filter Section - Horizontal Boxes (Only shown when showFilters is true) */}
        {showFilters && (
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2,
            mb: 3,
            alignItems: 'flex-start',
            flexWrap: 'wrap'
          }}>
            {/* Project Filter */}
            <Box sx={{ flex: 1, minWidth: 180 }}>
              <Typography
                sx={{
                  ...TYPOGRAPHY.BODY_SMALL,
                  color: COLORS.PRIMARY,
                  mb: 0.5,
                  fontWeight: 600
                }}
              >
                Project
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  multiple
                  value={selectedProjects}
                  onChange={(e) => {
                    setSelectedProjects(e.target.value);
                    setPage(1);
                  }}
                  open={filterMenusOpen.projects}
                  onOpen={() => setFilterMenusOpen(prev => ({ ...prev, projects: true }))}
                  onClose={() => setFilterMenusOpen(prev => ({ ...prev, projects: false }))}
                  input={<OutlinedInput />}
                  renderValue={(selected) => {
                    if (selected.length === 0) {
                      return (
                        <Typography sx={{
                          color: COLORS.LIGHT_SECONDARY,
                          fontSize: '0.875rem'
                        }}>
                          Select Projects
                        </Typography>
                      );
                    }
                    return selected.length <= 2
                      ? selected.map(id => {
                        const project = projects.find(p => p._id === id);
                        return project?.projectName || (id === state?.projectId ? state?.projectName : id);
                      }).join(', ')
                      : `${selected.length} selected`;
                  }}
                  displayEmpty
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: COLORS.PRIMARY,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: COLORS.PRIMARY,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: COLORS.PRIMARY,
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 250,
                        width: 280,
                        overflow: 'auto',
                        mt: 0.5,
                        '& .MuiMenuItem-root': {
                          minHeight: 40,
                          padding: '6px 12px',
                        },
                        '& .MuiCheckbox-root': {
                          padding: '6px',
                        }
                      }
                    },
                  }}
                >
                  {projects.length > 0 ? (
                    projects.map((project) => (
                      <MenuItem
                        key={project._id}
                        value={project._id}
                      >
                        <Checkbox
                          checked={selectedProjects.indexOf(project._id) > -1}
                          size="small"
                        />
                        <ListItemText
                          primary={project.projectName}
                          sx={{
                            margin: 0,
                            '& .MuiTypography-root': {
                              fontSize: '0.85rem',
                            }
                          }}
                        />
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>
                      <Typography sx={{
                        fontSize: '0.85rem',
                        color: COLORS.TEXT,
                        fontStyle: 'italic'
                      }}>
                        No projects available
                      </Typography>
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Box>

            {/* Engineer Filter - Only show for non-engineer roles */}
            {!isEngineerRole && (
              <Box sx={{ flex: 1, minWidth: 180 }}>
                <Typography
                  sx={{
                    ...TYPOGRAPHY.BODY_SMALL,
                    color: COLORS.PRIMARY,
                    mb: 0.5,
                    fontWeight: 600
                  }}
                >
                  Engineer
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    multiple
                    value={selectedEngineers}
                    onChange={(e) => {
                      setSelectedEngineers(e.target.value);
                      setPage(1);
                    }}
                    open={filterMenusOpen.engineers}
                    onOpen={() => setFilterMenusOpen(prev => ({ ...prev, engineers: true }))}
                    onClose={() => setFilterMenusOpen(prev => ({ ...prev, engineers: false }))}
                    input={<OutlinedInput />}
                    renderValue={(selected) => {
                      if (selected.length === 0) {
                        return (
                          <Typography sx={{
                            color: COLORS.LIGHT_SECONDARY,
                            fontSize: '0.875rem'
                          }}>
                            Select Engineers
                          </Typography>
                        );
                      }
                      return selected.length <= 2
                        ? selected.map(id => {
                          const engineer = engineers.find(e => e._id === id);
                          return engineer?.name || id;
                        }).join(', ')
                        : `${selected.length} selected`;
                    }}
                    displayEmpty
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: COLORS.PRIMARY,
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: COLORS.PRIMARY,
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: COLORS.PRIMARY,
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: 250,
                          width: 280,
                          overflow: 'auto',
                          mt: 0.5,
                          '& .MuiMenuItem-root': {
                            minHeight: 40,
                            padding: '6px 12px',
                          },
                          '& .MuiCheckbox-root': {
                            padding: '6px',
                          }
                        }
                      },
                    }}
                  >
                    {engineers.length > 0 ? (
                      engineers.map((engineer) => (
                        <MenuItem
                          key={engineer._id}
                          value={engineer._id}
                        >
                          <Checkbox
                            checked={selectedEngineers.indexOf(engineer._id) > -1}
                            size="small"
                          />
                          <ListItemText
                            primary={engineer.name}
                            sx={{
                              margin: 0,
                              '& .MuiTypography-root': {
                                fontSize: '0.85rem',
                              }
                            }}
                          />
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled>
                        <Typography sx={{
                          fontSize: '0.85rem',
                          color: COLORS.TEXT,
                          fontStyle: 'italic'
                        }}>
                          No engineers available
                        </Typography>
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Box>
            )}

            {/* Status Filter - UPDATED: Only show On Track and Report Missing */}
            <Box sx={{ flex: 1, minWidth: 180 }}>
              <Typography
                sx={{
                  ...TYPOGRAPHY.BODY_SMALL,
                  color: COLORS.PRIMARY,
                  mb: 0.5,
                  fontWeight: 600
                }}
              >
                Status
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  multiple
                  value={selectedStatuses}
                  onChange={(e) => {
                    setSelectedStatuses(e.target.value);
                    setPage(1);
                  }}
                  open={filterMenusOpen.statuses}
                  onOpen={() => setFilterMenusOpen(prev => ({ ...prev, statuses: true }))}
                  onClose={() => setFilterMenusOpen(prev => ({ ...prev, statuses: false }))}
                  input={<OutlinedInput />}
                  renderValue={(selected) => {
                    if (selected.length === 0) {
                      return (
                        <Typography sx={{
                          color: COLORS.LIGHT_SECONDARY,
                          fontSize: '0.875rem'
                        }}>
                          Select Statuses
                        </Typography>
                      );
                    }
                    return selected.length <= 2
                      ? selected.map(s => getStatusText(s)).join(', ')
                      : `${selected.length} selected`;
                  }}
                  displayEmpty
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: COLORS.PRIMARY,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: COLORS.PRIMARY,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: COLORS.PRIMARY,
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 250,
                        width: 280,
                        overflow: 'auto',
                        mt: 0.5,
                        '& .MuiMenuItem-root': {
                          minHeight: 40,
                          padding: '6px 12px',
                        },
                        '& .MuiCheckbox-root': {
                          padding: '6px',
                        }
                      }
                    },
                  }}
                >
                  {/* UPDATED: Only show On Track and Report Missing */}
                  {["ONTRACK", "REPORTMISSING"].map((status) => (
                    <MenuItem
                      key={status}
                      value={status}
                    >
                      <Checkbox
                        checked={selectedStatuses.indexOf(status) > -1}
                        size="small"
                      />
                      <ListItemText
                        primary={getStatusText(status)}
                        sx={{
                          margin: 0,
                          '& .MuiTypography-root': {
                            fontSize: '0.85rem',
                          }
                        }}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        )}

        {/* Error Message */}
        {error && !loading && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Table - Responsive Container */}
        <Paper
          elevation={0}
          sx={{
            backgroundColor: "transparent",
            overflow: "auto",
            width: "100%"
          }}
        >
          <TableContainer>
            <Table
              sx={{
                backgroundColor: "transparent",
                minWidth: isMobile ? 500 : "auto",
                "& thead th": {
                  backgroundColor: "transparent",
                },
                "& tbody td": {
                  backgroundColor: "transparent",
                  borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                },
              }}
            >
              <TableHead>
                <TableRow>
                  {getVisibleHeaders().map((header) => (
                    <TableCell
                      key={header.key}
                      sx={{
                        ...TYPOGRAPHY.H4,
                        color: COLORS.PRIMARY,
                        borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                        whiteSpace: "nowrap",
                        py: 2,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1,
                        }}
                      >
                        <span>{header.key}</span>
                        {header.sortable && (
                          <CustomTooltip title={
                            sortConfig.field === header.field
                              ? `Sorted ${sortConfig.direction === 'asc' ? 'Ascending' : 'Descending'}`
                              : `Sort by ${header.key}`
                          }>
                            <IconButton
                              onClick={() => handleSort(header.field)}
                              sx={{
                                padding: 0,
                                width: 24,
                                height: 24,
                              }}
                            >
                              <Icon
                                name={getSortIcon(header.field)}
                                color={
                                  sortConfig.field === header.field
                                    ? COLORS.ERROR
                                    : COLORS.LIGHT_SECONDARY
                                }
                              />
                            </IconButton>
                          </CustomTooltip>
                        )}
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={getVisibleHeaders().length} align="center" sx={{ py: 3 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : dprs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={getVisibleHeaders().length} align="center" sx={{ py: 3 }}>
                      <Typography sx={{ color: COLORS.TEXT }}>
                        {isFilterApplied()
                          ? "No DPRs found matching your filters"
                          : "No DPRs found"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  dprs.map((dpr) => (
                    <TableRow
                      key={dpr._id}
                      sx={{
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.02)',
                        }
                      }}
                    >
                      {getVisibleHeaders().map((header) => renderTableCell(header, dpr))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {!loading && dprs.length > 0 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: 2,
                p: 2,
              }}
            >
              <Typography sx={{ fontSize: 13, color: "#777" }}>
                {startIndex} - {endIndex} of {totalDprs}
              </Typography>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CustomTooltip title="Previous">
                  <span>
                    <IconButton
                      onClick={() => setPage(prev => Math.max(1, prev - 1))}
                      disabled={page === 1}
                      sx={{
                        width: 32,
                        height: 32,
                        color: page === 1 ? COLORS.LIGHT_SECONDARY : COLORS.PRIMARY,
                      }}
                    >
                      <Icon name="chevron-left" />
                    </IconButton>
                  </span>
                </CustomTooltip>

                {Array.from({ length: Math.min(totalPages, 3) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 3) {
                    pageNum = i + 1;
                  } else if (page === 1) {
                    pageNum = i + 1;
                  } else if (page === totalPages) {
                    pageNum = totalPages - 2 + i;
                  } else {
                    pageNum = page - 1 + i;
                  }

                  if (pageNum > 0 && pageNum <= totalPages) {
                    return (
                      <Button
                        key={pageNum}
                        onClick={() => setPage(pageNum)}
                        sx={{
                          minWidth: 32,
                          height: 32,
                          fontSize: 12,
                          fontWeight: 500,
                          bgcolor: page === pageNum ? COLORS.PRIMARY : "transparent",
                          color: page === pageNum ? "#fff" : COLORS.TEXT,
                          border: page === pageNum
                            ? "none"
                            : `1px solid ${COLORS.LIGHT_SECONDARY}`,
                          borderRadius: 1,
                          "&:hover": {
                            bgcolor: page === pageNum
                              ? COLORS.PRIMARY
                              : COLORS.LIGHT_BACKGROUND,
                          },
                        }}
                      >
                        {pageNum}
                      </Button>
                    );
                  }
                  return null;
                })}

                <CustomTooltip title="Next">
                  <span>
                    <IconButton
                      onClick={() => setPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={page === totalPages}
                      sx={{
                        width: 32,
                        height: 32,
                        color: page === totalPages ? COLORS.LIGHT_SECONDARY : COLORS.PRIMARY,
                      }}
                    >
                      <Icon name="chevron-right" />
                    </IconButton>
                  </span>
                </CustomTooltip>
              </Box>
            </Box>
          )}
        </Paper>

        {/* Global Sort Menu */}
        <Menu
          anchorEl={sortAnchorEl}
          open={Boolean(sortAnchorEl)}
          onClose={() => setSortAnchorEl(null)}
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: "0px 6px 18px rgba(0,0,0,0.15)",
              px: 0.5,
              minWidth: 160,
            },
          }}
        >
          {[
            { label: "Latest First", value: "LATEST", icon: "foldUp" },
            { label: "Oldest First", value: "OLDEST", icon: "foldDown" },
            { label: "Project Name A-Z", value: "AZ", icon: "sort" },
            { label: "Project Name Z-A", value: "ZA", icon: "sort" },
          ].map((item) => (
            <MenuItem
              key={item.value}
              onClick={() => handleGlobalSort(item.value)}
              sx={{
                gap: 1.5,
                borderRadius: 1,
                "&:hover": { bgcolor: "rgba(0, 0, 0, 0.04)" },
              }}
            >
              <Icon name={item.icon} color={COLORS.TEXT} />
              <Typography sx={TYPOGRAPHY.BODY_LARGE}>{item.label}</Typography>
            </MenuItem>
          ))}
        </Menu>

        {/* Calendar Menu */}
        <Menu
          anchorEl={calendarAnchorEl}
          open={Boolean(calendarAnchorEl)}
          onClose={() => setCalendarAnchorEl(null)}
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: "0px 6px 18px rgba(0,0,0,0.15)",
              px: 0.5,
            },
          }}
        >
          {[
            { label: "All Dates", value: "ALL", icon: "calendarAll" },
            { label: "Today", value: "TODAY", icon: "calendarToday" },
            { label: "Yesterday", value: "YESTERDAY", icon: "calendarYesterday" },
            { label: "Tomorrow", value: "TOMORROW", icon: "calendarTomorrow" },
            { label: "Custom Date Range", value: "CUSTOM", icon: "calendarCustom" },
          ].map((item) => {
            const isActive = dateFilter === item.value;

            return (
              <MenuItem
                key={item.value}
                onClick={() => handleCalendarFilterSelect(item.value)}
                sx={{
                  gap: 1.5,
                  borderRadius: 1,
                  color: isActive ? COLORS.PRIMARY : COLORS.TEXT,
                }}
              >
                <Icon
                  name={item.icon}
                  size={18}
                  color={isActive ? COLORS.PRIMARY : COLORS.TEXT}
                />
                <Typography sx={TYPOGRAPHY.BODY_LARGE}>
                  {item.label}
                </Typography>
              </MenuItem>
            );
          })}
        </Menu>

        {/* Custom Date Range Picker Dialog */}
        <Dialog
          open={showDatePicker}
          onClose={() => setShowDatePicker(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Icon name="calendarCustom" color={COLORS.PRIMARY} />
              <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY }}>
                Select Date Range
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
              <DatePicker
                label="From Date"
                value={customDateRange.from}
                onChange={(newValue) => {
                  setCustomDateRange(prev => ({ ...prev, from: newValue }));
                }}
                slotProps={{ textField: { fullWidth: true } }}
                maxDate={customDateRange.to || new Date()}
              />
              <DatePicker
                label="To Date"
                value={customDateRange.to}
                onChange={(newValue) => {
                  setCustomDateRange(prev => ({ ...prev, to: newValue }));
                }}
                slotProps={{ textField: { fullWidth: true } }}
                minDate={customDateRange.from}
                maxDate={new Date()}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button
              onClick={() => setShowDatePicker(false)}
              sx={{
                color: COLORS.TEXT,
                "&:hover": { bgcolor: "rgba(0,0,0,0.04)" }
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCustomDateRangeSelect}
              disabled={!customDateRange.from || !customDateRange.to}
              sx={{
                bgcolor: COLORS.SECONDARY,
                color: "#fff",
                "&:hover": { bgcolor: COLORS.SECONDARY },
                "&:disabled": { bgcolor: COLORS.LIGHT_SECONDARY }
              }}
            >
              Apply Date Range
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default DPR;